import { createSelector } from "reselect";
import { State } from "../../../redux/state";
import { TiktokDirectPostPayload } from "../interface/api/get-tiktok-creator-info";
import { initialDirectPostDraft, TiktokState } from "./reducer";

export const tiktokRoot = (state: State) => state.tiktok;

export const settingSelector = createSelector(
  tiktokRoot,
  (tiktok) => tiktok.settings
);

export const loginUrlSelector = createSelector(
  tiktokRoot,
  (tiktok) => tiktok.url
);

export const profilesSelector = createSelector(
  tiktokRoot,
  (tiktok) => tiktok.profiles
);

export const tiktokCreatorInfoSelector = createSelector(
  tiktokRoot,
  (t) => t.creatorInfo
);

export const tiktokCreatorInfoLoadingSelector = createSelector(
  tiktokRoot,
  (t) => t.creatorInfoLoading
);

export const tiktokCreatorInfoErrorSelector = createSelector(
  tiktokRoot,
  (t) => t.creatorInfoError
);

export const tiktokDirectPostDraftSelector = createSelector(
  tiktokRoot,
  (t) => t?.directPostDraft ?? initialDirectPostDraft
);

export function buildTiktokDirectPostPayload(
  tiktok: TiktokState | undefined | null,
  isVideo: boolean,
  videoDurationSec?: number | null
): TiktokDirectPostPayload | null {
  if (!tiktok) {
    return null;
  }
  const { creatorInfo } = tiktok;
  const d = tiktok.directPostDraft ?? initialDirectPostDraft;
  if (!creatorInfo?.privacy_level_options?.length) {
    return null;
  }
  if (!d.privacyLevel) {
    return null;
  }
  if (!creatorInfo.privacy_level_options.includes(d.privacyLevel)) {
    return null;
  }
  if (!d.musicUsageConsent) {
    return null;
  }
  if (d.commercialDisclosure && !d.brandOrganic && !d.brandContent) {
    return null;
  }
  if (d.brandContent && d.privacyLevel === "SELF_ONLY") {
    return null;
  }
  if (
    isVideo &&
    videoDurationSec != null &&
    videoDurationSec > 0 &&
    creatorInfo.max_video_post_duration_sec != null &&
    videoDurationSec > creatorInfo.max_video_post_duration_sec
  ) {
    return null;
  }
  return {
    privacyLevel: d.privacyLevel,
    allowComment: d.allowComment,
    allowDuet: d.allowDuet,
    allowStitch: d.allowStitch,
    commercialDisclosure: d.commercialDisclosure,
    brandOrganic: d.brandOrganic,
    brandContent: d.brandContent,
    musicUsageConsent: d.musicUsageConsent,
  };
}

export function getTiktokPublishBlockReason(
  tiktok: TiktokState | undefined | null,
  isVideo: boolean,
  videoDurationSec?: number | null
): string | null {
  if (!tiktok) {
    return "Cargando opciones de TikTok…";
  }
  const {
    creatorInfo,
    creatorInfoLoading,
    creatorInfoError,
    directPostDraft: rawDraft,
  } = tiktok;
  const d = rawDraft ?? initialDirectPostDraft;
  if (creatorInfoLoading) {
    return "Cargando opciones de TikTok…";
  }
  if (!creatorInfo?.privacy_level_options?.length) {
    return creatorInfoError
      ? "No se pudo cargar la configuración de TikTok. Reintenta o reconecta la cuenta."
      : "Conecta TikTok y espera a que carguen las opciones de publicación.";
  }
  if (!d.privacyLevel) {
    return "Elige la visibilidad de la publicación en TikTok (quién puede verla).";
  }
  if (!creatorInfo.privacy_level_options.includes(d.privacyLevel)) {
    return "La visibilidad elegida no está disponible para esta cuenta.";
  }
  if (!d.musicUsageConsent) {
    return "Marca la casilla de aceptación de uso de música / políticas de TikTok.";
  }
  if (d.commercialDisclosure && !d.brandOrganic && !d.brandContent) {
    return "Si indicas contenido comercial, elige “Tu marca” y/o “Contenido de marca”.";
  }
  if (d.brandContent && d.privacyLevel === "SELF_ONLY") {
    return "El contenido de marca no puede publicarse con visibilidad “Solo yo”.";
  }
  if (
    isVideo &&
    videoDurationSec != null &&
    videoDurationSec > 0 &&
    creatorInfo.max_video_post_duration_sec != null &&
    videoDurationSec > creatorInfo.max_video_post_duration_sec
  ) {
    return `El video supera la duración máxima permitida por TikTok para tu cuenta (${creatorInfo.max_video_post_duration_sec}s).`;
  }
  return null;
}
